import { tageZwischen } from './meintag'
import { alterInTagen, tageSpaeter, tagesschluessel, tagKompakt, werktageBis } from './zeit'

/**
 * Rechnerei hinter „Auf dem Schirm" ([Z-03](../../../docs/phasen/zusatz-03-auf-dem-schirm.md)).
 *
 * ## Warum das hier steht und nicht in der Ansicht
 *
 * Derselbe Grund wie bei `meintag.ts`: jede Funktion beantwortet eine Frage, die man **falsch**
 * beantworten kann, ohne dass es auffällt. „Ist dieser Pin heute fällig", „warte ich hier noch",
 * „fällt der Pin weg, weil die Aufgabe fertig ist". Eine falsche Antwort sieht aus wie eine
 * richtige – und wenn sie im JSX steht, gibt es keine Stelle, an der man sie prüfen könnte.
 *
 * Alles hier ist rein. **Kein `new Date()` ohne Übergabe**: die Wiedervorlage ist die eine
 * Funktion dieses Pakets, deren Verhalten von *morgen* abhängt, und ohne einen setzbaren
 * Stichtag ließe sie sich nur durch Warten prüfen.
 */

/* ─────────────────────────── Pins: Arten und Ziele ────────────────────────── */

/**
 * **`gepinnt` und `stumm` in einer Tabelle** ([E-211](../../../docs/ENTSCHEIDUNGEN.md#e-211)).
 *
 * Beides ist dieselbe Sorte Aussage – *was ein Mensch ausdrücklich über etwas gesagt hat*.
 * `stumm` heißt „hier warte ich nicht mehr auf Antwort" und gibt es nur für Aufgaben; ein
 * Vorhaben oder ein Bereich hat keine Warteliste, aus der man sich abmelden könnte.
 */
export const PIN_ARTEN = ['gepinnt', 'stumm'] as const
export type Pinart = (typeof PIN_ARTEN)[number]

/**
 * **Worauf ein Pin zeigt** – ein diskriminierter Verbund, damit sich ein halb gefülltes Ziel
 * gar nicht erst bauen lässt.
 *
 * In der Datenbank stehen dafür drei nullbare Spalten statt `ziel_art` + `ziel_id`: nur so
 * behalten Aufgabe und Vorhaben ihren Fremdschlüssel mit `ON DELETE CASCADE`. Ein polymorpher
 * Schlüssel hätte für alle drei keinen – und Pins, die auf gelöschte Aufgaben zeigen, räumt nie
 * jemand auf.
 *
 * > **Der Bereich ist die Ausnahme, und das ist keine Nachlässigkeit.** Bereiche haben keine
 * > Tabelle: ein Bereich *ist* das Paar (`task.source_id`, `task.source_scope`). Der
 * > Bereichs-Pin trägt es als Text und kann deshalb ins Leere zeigen, wenn ein Bereich aus der
 * > Konfiguration fällt. Die Ansicht fängt das ab, statt eine leere Zeile zu zeigen.
 */
export type Pinziel =
  | { art: 'aufgabe'; taskId: string }
  | { art: 'vorhaben'; vorhabenId: string }
  | { art: 'bereich'; sourceId: string; scope: string }

/** Eindeutiger Schlüssel eines Ziels – für `key`-Attribute und zum Vergleichen. */
export function pinzielSchluessel(ziel: Pinziel): string {
  switch (ziel.art) {
    case 'aufgabe':
      return `aufgabe:${ziel.taskId}`
    case 'vorhaben':
      return `vorhaben:${ziel.vorhabenId}`
    case 'bereich':
      return `bereich:${ziel.sourceId}:${ziel.scope}`
  }
}

/**
 * Ein Pin, wie ihn Server und Ansicht gemeinsam sehen.
 *
 * `wiedervorlage` ist ein **Tagesschlüssel** (`YYYY-MM-DD`), kein Zeitpunkt: eine Wiedervorlage
 * ist ein Tag, keine Uhrzeit. „Ab 2.9. 14:31" ist eine Genauigkeit, die niemand meint, und beim
 * ersten Zeitzonenwechsel eine Fehlerquelle ([E-216](../../../docs/ENTSCHEIDUNGEN.md#e-216)).
 */
export type Pin = {
  id: string
  art: Pinart
  ziel: Pinziel
  notiz: string | null
  wiedervorlage: string | null
  angelegtAm: Date
}

/* ─────────────────────────── Die Wiedervorlage ────────────────────────────── */

/**
 * **Der Zustand eines Pins an einem gegebenen Tag.**
 *
 * | | heißt |
 * |---|---|
 * | `sichtbar` | steht auf dem Schirm |
 * | `schlaeft` | Wiedervorlage liegt in der Zukunft – zählt in der leisen Zeile mit |
 * | `weg` | fällt still weg und wird nicht mehr angezeigt |
 *
 * Gerechnet, **nicht gespeichert**. Ein Zustandsfeld bräuchte einen Auftrag, der es umschaltet,
 * und ein Wecker, der einmal nicht läuft, verschluckt eine Wiedervorlage lautlos – man bemerkt
 * es Wochen später oder nie ([E-216](../../../docs/ENTSCHEIDUNGEN.md#e-216)).
 *
 * `erledigtAm` ist der Tagesschlüssel des Abschlusses, oder `null`, wenn das Ziel offen ist
 * (bei Vorhaben und Bereichen immer `null` – die werden nicht „fertig").
 */
export type Pinzustand = 'sichtbar' | 'schlaeft' | 'weg'

export function pinzustand(
  pin: { wiedervorlage: string | null },
  erledigtAm: string | null,
  heute: string,
): Pinzustand {
  const faellig = istFaellig(pin.wiedervorlage, heute)

  if (erledigtAm !== null) {
    /**
     * **Erledigtes räumt sich selbst weg – aber erst am Folgetag.** Man sieht das Häkchen noch
     * einmal, und die Pinnwand räumt sich auf, ohne dass etwas eine menschliche Entscheidung
     * löscht, während sie noch gilt.
     */
    if (erledigtAm < heute) return 'weg'
    /**
     * **Eine Wiedervorlage auf etwas inzwischen Erledigtem wacht nie auf.** Ein Wiedersehen mit
     * etwas Fertigem ist keine Erinnerung, sondern Papierkram.
     */
    return faellig ? 'sichtbar' : 'weg'
  }

  return faellig ? 'sichtbar' : 'schlaeft'
}

/** Ohne Datum ist ein Pin immer fällig; mit Datum ab diesem Tag. */
export function istFaellig(wiedervorlage: string | null, heute: string): boolean {
  return wiedervorlage === null || wiedervorlage <= heute
}

/**
 * **Heute wieder vorgelegt.** Nur am Tag selbst – danach ist der Pin ein Pin wie jeder andere.
 *
 * Ohne diese Markierung reiht sich ein aufgewachter Pin lautlos in eine Liste ein, die man
 * längst nur noch überfliegt, und das Datum war umsonst.
 */
export function istWeckruf(wiedervorlage: string | null, heute: string): boolean {
  return wiedervorlage !== null && wiedervorlage === heute
}

/**
 * Der nächste Tag, an dem etwas wieder vorliegt – für die leise Zeile unter der Zone.
 * `null`, wenn nichts schläft.
 */
export function naechsteWiedervorlage(
  pins: readonly { wiedervorlage: string | null }[],
  heute: string,
): string | null {
  let naechste: string | null = null
  for (const p of pins) {
    if (p.wiedervorlage === null || p.wiedervorlage <= heute) continue
    if (naechste === null || p.wiedervorlage < naechste) naechste = p.wiedervorlage
  }
  return naechste
}

/** Die fünf Vorgaben statt eines Kalenders – ein Kalender für „nächste Woche" ist zu viel Weg. */
export const WIEDERVORLAGE_VORGABEN = [
  { schluessel: 'morgen', label: 'morgen', tage: 1 },
  { schluessel: 'woche', label: 'nächste Woche', tage: 7 },
  { schluessel: 'zweiwochen', label: 'in zwei Wochen', tage: 14 },
  { schluessel: 'monat', label: 'nächsten Monat', tage: 30 },
] as const

/** Eine Vorgabe in einen Tagesschlüssel, gerechnet ab `heute` (als `Date`, wegen Sommerzeit). */
export function vorgabeZuTag(tage: number, heute: Date): string {
  return tagesschluessel(tageSpaeter(heute, tage))
}

/**
 * **„Wieder vorlegen in 1–5 Tagen"** (E-357, 16.09.2026) – die Knöpfe am Kommentarfeld und in
 * der Nachfassen-Zeile. Nils: *„Erinnere mich in 1, 2, 3, 4, 5 Tagen, dass ich die Aufgabe
 * wieder im Vorlauf habe."* Längeres geht über das Datumsfeld daneben.
 */
export const WIEDERVORLAGE_TAGE = [1, 2, 3, 4, 5] as const

/**
 * **Ist die Wiedervorlage einer wartenden Aufgabe da?** (E-315, seit E-357 im Vorlauf.)
 *
 * `<=` und nicht `===`: wer drei Tage nicht hineingesehen hat, soll die Zeile trotzdem finden.
 * Gerechnet, nicht geweckt – wie beim Pin (E-216): ein Takt, der einmal nicht läuft, verschluckt
 * sonst eine Erinnerung lautlos. Ein Datum an einer Aufgabe, die nicht mehr wartet, ist ein Rest.
 */
export function wiedervorlageFaellig(
  a: { status: string; wartenBis?: string | null },
  heute: string,
): boolean {
  return a.status === 'warten' && !!a.wartenBis && a.wartenBis <= heute
}

/* ─────────────────────────── Ich warte auf Antwort ────────────────────────── */

/**
 * **Wie weit „Ich warte" zurückreicht.**
 *
 * Ohne Karenzzeit nach vorn (Nils: *„sofort"*) braucht die Regel eine Grenze nach hinten. Sonst
 * stünde am ersten Tag jede alte Aufgabe darin, an der zufällig ich den letzten Kommentar
 * geschrieben habe – gemessen an einem Fall waren das **583**
 * ([E-183](../../../docs/ENTSCHEIDUNGEN.md)).
 *
 * Eine Frage, auf die ein Vierteljahr lang niemand geantwortet hat, wird nicht mehr beantwortet –
 * sie wird nachgefasst oder fallengelassen, und für beides ist die Zone der falsche Ort.
 *
 * **90, nicht 30** – Nils am 14.09.2026 zu [F-64](../../../docs/OFFENE-FRAGEN.md): *„bei F-64
 * 90 Tage."* Gebaut waren 30 als Vorschlag; drei Monate decken auch die Antwort ab, die nach dem
 * Urlaub beider Seiten noch kommt.
 */
export const WARTE_FENSTER_TAGE = 90

/**
 * Ab wann das Alter zur Warnfarbe wird – in **Werktagen**, nicht Kalendertagen. Wer freitags
 * fragt, wartet montags nicht „drei Tage"; das Wochenende ist keine Zeit, in der jemand nicht
 * geantwortet hätte.
 */
export const WARTE_WARNUNG_AB_WERKTAGEN = 5

export type Wartezeile = {
  taskId: string
  titel: string
  /** Wann ich zuletzt geschrieben habe. */
  seit: Date
  /** Kalendertage seit dem eigenen Kommentar – die Zahl in der Zeile. */
  tage: number
  /** Werktage – die Schwelle für die Warnfarbe. */
  werktage: number
  warnt: boolean
}

/**
 * **Der Ball liegt nicht mehr bei mir.**
 *
 * Die Regel ist mit Absicht schlicht: *der letzte Kommentar an der Aufgabe ist von mir, und die
 * Aufgabe ist offen.* Ausdrücklich **ohne Fragezeichen-Erkennung**
 * ([E-212](../../../docs/ENTSCHEIDUNGEN.md#e-212)) – die Hälfte der deutschen Rückfragen kommt
 * ohne `?` aus, und in einem Entwickler-Werkzeug ist `?` ein Operator und kein Satzzeichen. Eine
 * Automatik, die zu 80 % richtig liegt, ist hier schlechter als die einfache Regel: bei 80 %
 * muss man jede Zeile prüfen, und dann kann man auch gleich die Aufgabenliste lesen.
 *
 * **Drei Wege hinaus**, alle über `pin`: ein sichtbarer Pin (die Zeile steht schon oben),
 * `stumm` für immer, und die Wiedervorlage bis zum Datum. *„Ich sehe am 2. September nach"* ist
 * die stärkere Aussage als *„der Ball liegt woanders"*; beides gleichzeitig zu zeigen hieße,
 * dieselbe Aufgabe zweimal auf den Schirm zu legen.
 *
 * Ältestes zuerst – das ist die Zeile, bei der Nachhaken am ehesten fällig ist.
 */
export function wartetAufAntwort(
  aufgaben: readonly {
    taskId: string
    titel: string
    status: string
    /** Verfasser des **jüngsten** Kommentars; `null` bei unbekannter Person. */
    letzterKommentarVon: string | null
    letzterKommentarAm: Date
  }[],
  lage: {
    ichId: string
    jetzt: Date
    /**
     * **Aufgaben mit sichtbarem Pin.** Sie stehen schon zwei Zentimeter weiter oben; sie hier
     * zu wiederholen wäre dieselbe Zeile zweimal auf demselben Bildschirm. Genau der Fehler,
     * den Z-02.5 in „Nebenbei" gefangen hat – zwei Zonen, die dasselbe zeigen, sind eine Zone
     * zu viel.
     */
    gepinnt: ReadonlySet<string>
    /** Aufgaben mit `art = 'stumm'` – „erledigt sich", für immer. */
    stumm: ReadonlySet<string>
    /** Aufgaben mit einem noch schlafenden Pin – bis zum Datum kein Warten. */
    schlafend: ReadonlySet<string>
  },
): Wartezeile[] {
  const grenze = tageSpaeter(lage.jetzt, -WARTE_FENSTER_TAGE)

  return aufgaben
    .filter((a) => {
      if (a.letzterKommentarVon !== lage.ichId) return false
      if (a.status === 'erledigt' || a.status === 'zurueckgestellt') return false
      if (a.letzterKommentarAm < grenze) return false
      if (lage.gepinnt.has(a.taskId)) return false
      if (lage.stumm.has(a.taskId)) return false
      if (lage.schlafend.has(a.taskId)) return false
      return true
    })
    .map((a) => {
      const tage = alterInTagen(a.letzterKommentarAm, lage.jetzt)
      const werktage = werktageBis(lage.jetzt, a.letzterKommentarAm)
      return {
        taskId: a.taskId,
        titel: a.titel,
        seit: a.letzterKommentarAm,
        tage,
        werktage,
        warnt: werktage >= WARTE_WARNUNG_AB_WERKTAGEN,
      }
    })
    .sort((a, b) => a.seit.getTime() - b.seit.getTime())
}

/* ─────────────────────────── Auskunft in Worten ───────────────────────────── */

/** „seit heute" · „seit gestern" · „seit 6 Tagen". */
export function seitText(tage: number): string {
  if (tage <= 0) return 'seit heute'
  if (tage === 1) return 'seit gestern'
  return `seit ${tage} Tagen`
}

/**
 * Was in der Zeile eines aufgewachten Pins steht: am Tag selbst der Weckruf, danach das Datum.
 * `null` für Pins ohne Wiedervorlage – die tragen ihre Notiz und sonst nichts.
 */
export function wiedervorlageText(wiedervorlage: string | null, heute: string): string | null {
  if (wiedervorlage === null) return null
  if (istWeckruf(wiedervorlage, heute)) return 'heute wieder vorgelegt'
  if (wiedervorlage > heute) return `liegt wieder vor am ${tagKompakt(wiedervorlage)}`
  return `seit ${tagKompakt(wiedervorlage)}`
}

/**
 * **Wie lange eine fällige Wiedervorlage schon liegt**, in Kalendertagen. Negativ wäre sinnlos,
 * deshalb `0` für alles, was noch schläft oder heute erst aufwacht.
 */
export function ueberfaelligSeit(wiedervorlage: string | null, heute: string): number {
  if (wiedervorlage === null || wiedervorlage >= heute) return 0
  return tageZwischen(wiedervorlage, heute)
}

/* ─────────────────────────── Sortierung der Pin-Gruppe ────────────────────── */

/**
 * **Weckrufe zuerst, dann das zuletzt Angepinnte.**
 *
 * Der Weckruf steht oben, weil er heute etwas von mir will; alles andere ist eine Pinnwand, und
 * eine Pinnwand ordnet sich nach dem, was zuletzt dazukam. Erledigtes rutscht ans Ende – es
 * verschwindet ohnehin morgen.
 */
export function pinReihenfolge<T extends { wiedervorlage: string | null; angelegtAm: Date }>(
  pins: readonly T[],
  heute: string,
  istErledigt: (pin: T) => boolean = () => false,
): T[] {
  return [...pins].sort((a, b) => {
    const erledigtA = istErledigt(a) ? 1 : 0
    const erledigtB = istErledigt(b) ? 1 : 0
    if (erledigtA !== erledigtB) return erledigtA - erledigtB

    const weckA = istWeckruf(a.wiedervorlage, heute) ? 0 : 1
    const weckB = istWeckruf(b.wiedervorlage, heute) ? 0 : 1
    if (weckA !== weckB) return weckA - weckB

    return b.angelegtAm.getTime() - a.angelegtAm.getTime()
  })
}
